import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Input,
  Skeleton,
  Tabs,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  Captions,
  Clipboard,
  Download,
  FileText,
  Link as LinkIcon,
  ListFilter,
  Loader2,
  Mic,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Search,
  Sparkles,
  Square,
  UploadCloud,
  Video,
  Volume2,
  VolumeX,
} from "lucide-react";

import { appConfig } from "@/config/app.config";
import { buildTranscriptMarkdown, downloadTranscript } from "@/lib/exporters";
import { countWords, formatTimestamp, transcriptToText } from "@/lib/format";
import {
  detectPremiumSocialMediaLink,
  filenameFromBlob,
  formatBytes,
  validateDirectMediaUrl,
  validateMediaFile,
} from "@/lib/media";
import { generateTranscript, summarizeTranscript } from "@/services/api";
import { useTranscriptStore } from "@/store/transcript.store";
import { isPremiumWaitlistJoined, triggerPremiumGate } from "@/lib/waitlist";
import type { MediaSourceMode } from "@/types/transcript";

function modeLabel(mode: MediaSourceMode): string {
  if (mode === "upload") return "Upload";
  if (mode === "record") return "Record";
  return "Link";
}

function createRecordingFilename(): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `recording-${timestamp}.webm`;
}

function getMediaPolicyError(): string {
  const browserDocument = document as Document & {
    featurePolicy?: { allowsFeature?: (feature: string) => boolean };
    permissionsPolicy?: { allowsFeature?: (feature: string) => boolean };
  };
  const policy =
    browserDocument.permissionsPolicy || browserDocument.featurePolicy;
  if (typeof policy?.allowsFeature !== "function") return "";

  try {
    const cameraAllowed = policy.allowsFeature("camera");
    const microphoneAllowed = policy.allowsFeature("microphone");
    if (cameraAllowed && microphoneAllowed) return "";
  } catch {
    return "";
  }

  return "Camera or microphone access is blocked by this page's permissions policy. Open the app directly in a browser tab or allow camera and microphone for this site.";
}

export function TranscriptTool() {
  const {
    mode,
    title,
    mediaUrl,
    selectedFile,
    selectedFileMeta,
    recordedBlob,
    recordedBlobMeta,
    recordPreviewUrl,
    isRecording,
    uploadPreviewUrl,
    isUploadPlaying,
    isUploadMuted,
    isRecordMuted,
    isHydratingMedia,
    query,
    validationError,
    storageWarning,
    result,
    summary,
    setMode,
    setTitle,
    setMediaUrl,
    setSelectedFile,
    setRecordedBlob,
    setIsRecording,
    setIsUploadPlaying,
    setIsUploadMuted,
    setIsRecordMuted,
    setQuery,
    setValidationError,
    setResult,
    setSummary,
    hydrateStoredMedia,
    clearExpiredTranscriptCache,
    resetTranscriptStore,
  } = useTranscriptStore();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const uploadVideoRef = useRef<HTMLVideoElement | null>(null);
  const resultRef = useRef<HTMLDivElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const recordingBytesRef = useRef(0);
  const transcriptRequestIdRef = useRef(0);
  const summaryRequestIdRef = useRef(0);
  const premiumSocialPromptRef = useRef("");
  const [recordingSizeBytes, setRecordingSizeBytes] = useState(0);
  const [recordingLimitWarning, setRecordingLimitWarning] = useState("");

  const transcriptMutation = useMutation({
    mutationFn: generateTranscript,
    onMutate: () => {
      transcriptRequestIdRef.current += 1;
      return { requestId: transcriptRequestIdRef.current };
    },
    onSuccess: (data, _variables, context) => {
      if (context?.requestId !== transcriptRequestIdRef.current) return;
      setResult(data);
      setSummary(null);
      setQuery("");
      message.success("Transcript generated.");
      setTimeout(() => {
        resultRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    },
  });

  const summaryMutation = useMutation({
    mutationFn: summarizeTranscript,
    onMutate: () => {
      summaryRequestIdRef.current += 1;
      return { requestId: summaryRequestIdRef.current };
    },
    onSuccess: (data, _variables, context) => {
      if (context?.requestId !== summaryRequestIdRef.current) return;
      setSummary(data);
      message.success("AI notes ready.");
    },
  });

  useEffect(() => {
    clearExpiredTranscriptCache();
    void hydrateStoredMedia();
  }, [clearExpiredTranscriptCache, hydrateStoredMedia]);

  useEffect(() => {
    return () => {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.ondataavailable = null;
        recorderRef.current.onstop = null;
        recorderRef.current.stop();
      }
      recorderRef.current = null;
      chunksRef.current = [];
      setIsRecording(false);
      setIsUploadPlaying(false);
      stopStream();
    };
  }, [setIsRecording, setIsUploadPlaying]);

  const activeFile = mode === "record" ? recordedBlob : selectedFile;
  const maxMediaBytes = appConfig.limits.maxUploadMb * 1024 * 1024;
  const recordedDisplayName = recordedBlob
    ? filenameFromBlob(recordedBlob, "recorded-video.webm")
    : recordedBlobMeta?.name || "";
  const activeFilename =
    activeFile && mode === "record"
      ? recordedDisplayName
      : selectedFile
        ? selectedFile.name
        : "";
  const uploadDisplayName = selectedFile?.name || selectedFileMeta?.name || "";
  const isRecordingOverLimit = recordingSizeBytes > maxMediaBytes;
  const isRecordedBlobOverLimit = Boolean(
    recordedBlob && recordedBlob.size > maxMediaBytes,
  );

  const filteredSegments = useMemo(() => {
    if (!result) return [];
    const term = query.trim().toLowerCase();
    if (!term) return result.segments;
    return result.segments.filter((segment) =>
      segment.text.toLowerCase().includes(term),
    );
  }, [query, result]);

  const transcriptText = result ? transcriptToText(result.segments) : "";
  const canSummarize = Boolean(result?.text && !summaryMutation.isPending);

  function stopStream() {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    setValidationError("");
    if (file && !title) {
      setTitle(file.name.replace(/\.[^.]+$/, ""));
    }
    event.target.value = "";
  }

  function premiumSocialLinkMessage(provider: "TikTok" | "Instagram"): string {
    const label = provider === "Instagram" ? "Instagram Reels" : "TikTok";
    if (isPremiumWaitlistJoined()) {
      return `${label} link transcription is coming for premium users. You're already on the waitlist; use upload, record, or a direct media file URL today.`;
    }

    return `${label} link transcription is coming for premium users. Join the waitlist for early access, or use upload, record, or a direct media file URL today.`;
  }

  function promptForPremiumSocialLink(value: string): boolean {
    const provider = detectPremiumSocialMediaLink(value);
    if (!provider) {
      premiumSocialPromptRef.current = "";
      return false;
    }

    const trimmed = value.trim();
    if (premiumSocialPromptRef.current !== trimmed) {
      premiumSocialPromptRef.current = trimmed;
      const notice = premiumSocialLinkMessage(provider);
      message.info({
        content: notice,
        duration: 5,
        key: "premium-social-link",
      });
      if (!isPremiumWaitlistJoined()) {
        triggerPremiumGate();
      }
    }

    setValidationError(premiumSocialLinkMessage(provider));
    return true;
  }

  function handleMediaUrlChange(value: string) {
    setMediaUrl(value);
    if (!value.trim()) {
      premiumSocialPromptRef.current = "";
      setValidationError("");
      return;
    }
    if (promptForPremiumSocialLink(value)) return;
    setValidationError("");
  }

  function recordingLimitMessage(sizeBytes: number): string {
    return `This recording is already ${formatBytes(sizeBytes)}, over the ${appConfig.limits.maxUploadMb} MB free limit. It will be rejected unless you retake a shorter clip.`;
  }

  async function startRecording(options: { retake?: boolean } = {}) {
    setValidationError("");
    setRecordingSizeBytes(0);
    setRecordingLimitWarning("");
    recordingBytesRef.current = 0;
    if (
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      setValidationError(
        "This browser cannot record video. Use upload or direct link instead.",
      );
      return;
    }

    const policyError = getMediaPolicyError();
    if (policyError) {
      setValidationError(policyError);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      if (options.retake || recordedBlob) {
        setRecordedBlob(null);
        setResult(null);
        setSummary(null);
      }

      chunksRef.current = [];
      const recorderOptions =
        typeof MediaRecorder.isTypeSupported === "function" &&
        MediaRecorder.isTypeSupported("video/webm")
          ? { mimeType: "video/webm" }
          : undefined;
      const recorder = new MediaRecorder(stream, recorderOptions);
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data.size <= 0) return;

        chunksRef.current.push(event.data);
        const nextSize = recordingBytesRef.current + event.data.size;
        recordingBytesRef.current = nextSize;
        setRecordingSizeBytes(nextSize);

        if (nextSize > maxMediaBytes) {
          const warning = recordingLimitMessage(nextSize);
          setRecordingLimitWarning(warning);
          message.warning({
            content: warning,
            duration: 4,
            key: "recording-size-limit",
          });
        }
      };
      recorder.onstop = () => {
        const filename = createRecordingFilename();
        const blob = new File(chunksRef.current, filename, {
          type: recorder.mimeType || "video/webm",
          lastModified: Date.now(),
        });
        chunksRef.current = [];
        setRecordedBlob(blob);
        setRecordingSizeBytes(blob.size);
        if (blob.size > maxMediaBytes) {
          setRecordingLimitWarning(recordingLimitMessage(blob.size));
        }
        stopStream();
      };
      recorder.start(1000);
      setIsRecording(true);
    } catch (error) {
      stopStream();
      const blockedByPolicy =
        error instanceof DOMException &&
        ["SecurityError", "NotAllowedError"].includes(error.name) &&
        getMediaPolicyError();
      setValidationError(
        blockedByPolicy ||
          "Camera or microphone access was blocked. Allow camera and microphone access in your browser, then try recording again.",
      );
    }
  }

  function stopRecording() {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    recorderRef.current = null;
    setIsRecording(false);
  }

  function retakeRecording() {
    void startRecording({ retake: true });
  }

  function buildTranscriptRequest() {
    if (mode === "link") {
      if (promptForPremiumSocialLink(mediaUrl)) {
        return null;
      }

      const validation = validateDirectMediaUrl(mediaUrl);
      if (!validation.ok) {
        setValidationError(validation.error);
        return null;
      }
      return {
        sourceType: "link" as const,
        mediaUrl: validation.mediaUrl,
        title: title || undefined,
      };
    }

    const validation = validateMediaFile(activeFile);
    if (!validation.ok) {
      setValidationError(validation.error);
      return null;
    }

    return {
      sourceType: mode,
      file: activeFile!,
      filename: filenameFromBlob(
        activeFile!,
        activeFilename || "video-transcript-media.webm",
      ),
      title: title || activeFilename || undefined,
    };
  }

  function handleGenerate() {
    const request = buildTranscriptRequest();
    if (!request) return;

    setValidationError("");
    transcriptMutation.mutate(request);
  }

  async function handleCopy(content: string, label: string) {
    await navigator.clipboard.writeText(content);
    message.success(`${label} copied.`);
  }

  function resetTool() {
    transcriptRequestIdRef.current += 1;
    summaryRequestIdRef.current += 1;
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.ondataavailable = null;
      recorderRef.current.onstop = null;
      recorderRef.current.stop();
    }
    recorderRef.current = null;
    chunksRef.current = [];
    recordingBytesRef.current = 0;
    setRecordingSizeBytes(0);
    setRecordingLimitWarning("");
    stopStream();
    resetTranscriptStore();
    transcriptMutation.reset();
    summaryMutation.reset();
  }

  const summaryText = summary
    ? [
        summary.summary,
        "",
        "Key takeaways",
        ...summary.keyPoints.map((point) => `- ${point}`),
        "",
        "Action items",
        ...summary.actionItems.map((point) => `- ${point}`),
        "",
        "Notes",
        ...summary.notes.map((point) => `- ${point}`),
        "",
        "Chapters",
        ...summary.chapters.map((point) => `- ${point}`),
      ].join("\n")
    : "";

  return (
    <section className="transcript-tool w-full max-w-full overflow-hidden border border-neutral-200 bg-white shadow-line dark:border-neutral-800 dark:bg-ink-900">
      <div>
        <div className="border-b border-neutral-200 p-4 dark:border-neutral-800 sm:p-6">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <Tag
                bordered={false}
                className="mb-3 rounded-none bg-neutral-100 px-2 py-1 text-neutral-700 dark:bg-neutral-900 dark:text-neutral-300"
              >
                Free video transcription
              </Tag>
              <h2 className="text-2xl font-semibold tracking-normal sm:text-3xl">
                Turn video into text
              </h2>
              <p className="mt-2 max-w-xl text-sm leading-6 text-neutral-600 dark:text-neutral-400">
                Upload a file, record in the browser, or paste a direct media
                link.
              </p>
            </div>
            <Tooltip title="Reset">
              <Button
                icon={<RefreshCw className="h-4 w-4" />}
                onClick={resetTool}
                aria-label="Reset transcript tool"
                className="self-start"
              />
            </Tooltip>
          </div>

          <Tabs
            className="transcript-source-tabs"
            tabBarGutter={0}
            activeKey={mode}
            onChange={(key) => {
              setMode(key as MediaSourceMode);
              setValidationError("");
            }}
            items={[
              {
                key: "upload",
                label: (
                  <span className="inline-flex items-center gap-2">
                    <UploadCloud className="h-4 w-4" />
                    Upload
                  </span>
                ),
                children: (
                  <div className="space-y-3">
                    <input
                      id="video-upload-input"
                      type="file"
                      accept="audio/*,video/*,.mp4,.mov,.webm,.mp3,.m4a,.wav,.ogg"
                      className="sr-only"
                      onChange={handleFileChange}
                    />
                    <label
                      htmlFor="video-upload-input"
                      className="flex min-h-36 cursor-pointer flex-col items-center justify-center border border-dashed border-neutral-300 p-5 text-center transition hover:border-neutral-950 dark:border-neutral-700 dark:hover:border-white"
                    >
                      <UploadCloud className="h-8 w-8 text-neutral-500" />
                      <span className="mt-3 text-sm font-medium">
                        {uploadDisplayName
                          ? uploadDisplayName
                          : "Choose video or audio file"}
                      </span>
                      <span className="mt-1 text-xs text-neutral-500">
                        MP4, MOV, WebM, MP3, M4A, WAV, OGG up to{" "}
                        {appConfig.limits.maxUploadMb} MB
                      </span>
                    </label>
                    {uploadPreviewUrl ? (
                      <div className="space-y-2">
                        <div className="relative overflow-hidden border border-neutral-200 bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900">
                          <video
                            ref={uploadVideoRef}
                            src={uploadPreviewUrl}
                            playsInline
                            onPlay={() => setIsUploadPlaying(true)}
                            onPause={() => setIsUploadPlaying(false)}
                            onEnded={() => setIsUploadPlaying(false)}
                            onVolumeChange={() =>
                              setIsUploadMuted(
                                uploadVideoRef.current?.muted ?? false,
                              )
                            }
                            className="aspect-video w-full bg-neutral-950 object-contain"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (uploadVideoRef.current) {
                                const next = !isUploadMuted;
                                uploadVideoRef.current.muted = next;
                                setIsUploadMuted(next);
                              }
                            }}
                            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center bg-black/50 text-white transition-colors hover:bg-black/70"
                            aria-label={isUploadMuted ? "Unmute" : "Mute"}
                          >
                            {isUploadMuted ? (
                              <VolumeX className="h-3.5 w-3.5" />
                            ) : (
                              <Volume2 className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <Button
                            onClick={() => {
                              if (isUploadPlaying) {
                                uploadVideoRef.current?.pause();
                              } else {
                                void uploadVideoRef.current?.play();
                              }
                            }}
                            icon={
                              isUploadPlaying ? (
                                <Pause className="h-4 w-4" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )
                            }
                          >
                            {isUploadPlaying ? "Pause" : "Play"}
                          </Button>
                          <Button
                            onClick={() => {
                              if (uploadVideoRef.current) {
                                uploadVideoRef.current.pause();
                                uploadVideoRef.current.currentTime = 0;
                                setIsUploadPlaying(false);
                              }
                            }}
                            icon={<Square className="h-4 w-4" />}
                          >
                            Stop
                          </Button>
                          <Button
                            onClick={() => {
                              if (uploadVideoRef.current) {
                                uploadVideoRef.current.currentTime = 0;
                                void uploadVideoRef.current.play();
                              }
                            }}
                            icon={<RotateCcw className="h-4 w-4" />}
                          >
                            Restart
                          </Button>
                        </div>
                      </div>
                    ) : null}
                    {selectedFile ? (
                      <p className="text-xs text-neutral-500">
                        {formatBytes(selectedFile.size)} selected and ready.
                      </p>
                    ) : isHydratingMedia && selectedFileMeta ? (
                      <p className="text-xs text-neutral-500">
                        Restoring {selectedFileMeta.name}.
                      </p>
                    ) : null}
                  </div>
                ),
              },
              {
                key: "record",
                label: (
                  <span className="inline-flex items-center gap-2">
                    <Video className="h-4 w-4" />
                    Record
                  </span>
                ),
                children: (
                  <div className="space-y-3">
                    <div className="relative overflow-hidden border border-neutral-200 bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900">
                      <video
                        ref={videoRef}
                        src={recordPreviewUrl || undefined}
                        controls={Boolean(recordPreviewUrl && !isRecording)}
                        muted={isRecording}
                        playsInline
                        onVolumeChange={() =>
                          setIsRecordMuted(videoRef.current?.muted ?? false)
                        }
                        className="aspect-video w-full bg-neutral-950 object-cover"
                      />
                      {recordPreviewUrl && !isRecording ? (
                        <button
                          type="button"
                          onClick={() => {
                            if (videoRef.current) {
                              const next = !isRecordMuted;
                              videoRef.current.muted = next;
                              setIsRecordMuted(next);
                            }
                          }}
                          className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center bg-black/50 text-white transition-colors hover:bg-black/70"
                          aria-label={isRecordMuted ? "Unmute" : "Mute"}
                        >
                          {isRecordMuted ? (
                            <VolumeX className="h-3.5 w-3.5" />
                          ) : (
                            <Volume2 className="h-3.5 w-3.5" />
                          )}
                        </button>
                      ) : null}
                    </div>
                    <div
                      className={
                        recordedBlob
                          ? "grid gap-3 sm:grid-cols-3"
                          : "grid gap-3 sm:grid-cols-2"
                      }
                    >
                      <Button
                        onClick={() => void startRecording()}
                        disabled={isRecording || transcriptMutation.isPending}
                        icon={<Mic className="h-4 w-4" />}
                      >
                        Start Recording
                      </Button>
                      <Button
                        onClick={stopRecording}
                        disabled={!isRecording}
                        icon={<Square className="h-4 w-4" />}
                      >
                        Stop Recording
                      </Button>
                      {recordedBlob ? (
                        <Button
                          onClick={retakeRecording}
                          disabled={isRecording || transcriptMutation.isPending}
                          icon={<RotateCcw className="h-4 w-4" />}
                        >
                          Retake Recording
                        </Button>
                      ) : null}
                    </div>
                    {isRecording ? (
                      <p
                        className={
                          isRecordingOverLimit
                            ? "text-xs font-medium text-red-600 dark:text-red-400"
                            : "text-xs text-neutral-500"
                        }
                      >
                        {formatBytes(recordingSizeBytes)} recorded of{" "}
                        {appConfig.limits.maxUploadMb} MB free limit.
                      </p>
                    ) : null}
                    {recordingLimitWarning && isRecording ? (
                      <Alert
                        className="rounded-none"
                        type="warning"
                        showIcon
                        message={recordingLimitWarning}
                      />
                    ) : null}
                    {recordedBlob ? (
                      <p className="text-xs text-neutral-500">
                        {recordedDisplayName} - {formatBytes(recordedBlob.size)}{" "}
                        recording ready to transcribe.
                      </p>
                    ) : null}
                    {recordedBlob && isRecordedBlobOverLimit ? (
                      <Alert
                        className="rounded-none"
                        type="error"
                        showIcon
                        message={`${recordedDisplayName} is ${formatBytes(recordedBlob.size)}, over the ${appConfig.limits.maxUploadMb} MB free limit. Retake a shorter clip before generating a transcript.`}
                      />
                    ) : null}
                  </div>
                ),
              },
              {
                key: "link",
                label: (
                  <span className="inline-flex items-center gap-2">
                    <LinkIcon className="h-4 w-4" />
                    Link
                  </span>
                ),
                children: (
                  <Input.TextArea
                    value={mediaUrl}
                    onChange={(event) => handleMediaUrlChange(event.target.value)}
                    onPaste={(event) => {
                      const pasted = event.clipboardData.getData("text");
                      promptForPremiumSocialLink(pasted);
                    }}
                    placeholder="https://example.com/video.mp4"
                    autoSize={{ minRows: 3, maxRows: 4 }}
                    status={
                      validationError && mode === "link" ? "error" : undefined
                    }
                    className="rounded-none"
                    aria-label="Direct video or audio URL"
                  />
                ),
              },
            ]}
          />

          <div className="mt-4 space-y-3">
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Optional transcript title"
              aria-label="Transcript title"
            />
            <div className="grid gap-3">
              <Button
                type="primary"
                size="large"
                onClick={handleGenerate}
                disabled={
                  transcriptMutation.isPending || isRecording || isHydratingMedia
                }
                icon={
                  transcriptMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )
                }
                className="w-full rounded-none bg-neutral-950 font-medium hover:!bg-neutral-700 dark:bg-white dark:text-neutral-950 dark:hover:!bg-neutral-200"
              >
                {transcriptMutation.isPending
                  ? "Generating..."
                  : "Generate Transcript"}
              </Button>
            </div>
          </div>

          {validationError ? (
            <Alert
              className="mt-4 rounded-none"
              type="error"
              showIcon
              message={validationError}
            />
          ) : null}
          {storageWarning ? (
            <Alert
              className="mt-4 rounded-none"
              type="warning"
              showIcon
              message={storageWarning}
            />
          ) : null}
          {transcriptMutation.error ? (
            <Alert
              className="mt-4 rounded-none"
              type="error"
              showIcon
              message={transcriptMutation.error.message}
            />
          ) : null}

          <div className="mt-6 grid grid-cols-3 gap-2 border-t border-neutral-200 pt-4 text-sm dark:border-neutral-800">
            <div>
              <p className="text-neutral-500">Limit</p>
              <p className="font-medium">{appConfig.limits.maxUploadMb} MB</p>
            </div>
            <div>
              <p className="text-neutral-500">Storage</p>
              <p className="font-medium">Local only</p>
            </div>
            <div>
              <p className="text-neutral-500">Exports</p>
              <p className="font-medium">TXT, MD, PDF</p>
            </div>
          </div>
        </div>

        {transcriptMutation.isPending || result ? (
          <div ref={resultRef} className="p-4 sm:p-6">
            <div className="mb-4 flex flex-col gap-3 border-b border-neutral-200 pb-4 dark:border-neutral-800 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-base font-semibold">Transcript result</h3>
                <p className="text-sm text-neutral-500">
                  {result
                    ? `${result.segments.length} segments, ${countWords(result.text)} words`
                    : `${modeLabel(mode)} is ready when you are.`}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Tooltip title="Copy transcript">
                  <Button
                    icon={<Clipboard className="h-4 w-4" />}
                    disabled={!result}
                    onClick={() =>
                      result && handleCopy(transcriptText, "Transcript")
                    }
                    aria-label="Copy transcript"
                  />
                </Tooltip>
                <Tooltip title="Download TXT">
                  <Button
                    icon={<Download className="h-4 w-4" />}
                    disabled={!result}
                    onClick={() => result && downloadTranscript(result, "txt")}
                    aria-label="Download TXT"
                  />
                </Tooltip>
                <Tooltip title="Download Markdown">
                  <Button
                    disabled={!result}
                    onClick={() => result && downloadTranscript(result, "md")}
                    aria-label="Download Markdown"
                  >
                    MD
                  </Button>
                </Tooltip>
                <Tooltip title="Download captions">
                  <Button
                    icon={<Captions className="h-4 w-4" />}
                    disabled={!result}
                    onClick={() => result && downloadTranscript(result, "vtt")}
                    aria-label="Download VTT captions"
                  />
                </Tooltip>
                <Tooltip title="Download PDF">
                  <Button
                    disabled={!result}
                    onClick={() => result && downloadTranscript(result, "pdf")}
                    aria-label="Download PDF"
                  >
                    PDF
                  </Button>
                </Tooltip>
              </div>
            </div>

            {transcriptMutation.isPending ? (
              <div className="space-y-4">
                <Skeleton active paragraph={{ rows: 2 }} />
                <Skeleton active paragraph={{ rows: 8 }} />
              </div>
            ) : null}

            {result ? (
              <Tabs
                defaultActiveKey="transcript"
                items={[
                  {
                    key: "transcript",
                    label: "Transcript",
                    children: (
                      <div className="space-y-4">
                        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                          <Input
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            prefix={
                              <Search className="h-4 w-4 text-neutral-400" />
                            }
                            placeholder="Search within transcript"
                            aria-label="Search transcript"
                          />
                          <Tag className="m-0 flex h-8 items-center rounded-none px-3">
                            <ListFilter className="mr-1 h-3.5 w-3.5" />
                            {filteredSegments.length} shown
                          </Tag>
                        </div>

                        <div className="max-h-[420px] overflow-auto border border-neutral-200 dark:border-neutral-800">
                          {filteredSegments.map((segment) => (
                            <div
                              key={`${segment.start}-${segment.text}`}
                              className="grid grid-cols-[76px_1fr] gap-3 border-b border-neutral-100 px-3 py-3 text-sm last:border-b-0 dark:border-neutral-900"
                            >
                              <span className="font-mono text-xs text-neutral-500">
                                {formatTimestamp(segment.start)}
                              </span>
                              <p className="leading-6 text-neutral-800 dark:text-neutral-200">
                                {segment.text}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: "summary",
                    label: "AI Notes",
                    children: (
                      <div className="space-y-4">
                        <div className="flex flex-col gap-3 border border-neutral-200 p-4 dark:border-neutral-800 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <h4 className="font-medium">
                              Summary, notes, and chapters
                            </h4>
                            <p className="text-sm text-neutral-500">
                              Create a quick summary, takeaways, action items,
                              notes, and chapters.
                            </p>
                          </div>
                          <Button
                            type="primary"
                            disabled={!canSummarize}
                            loading={summaryMutation.isPending}
                            icon={<Sparkles className="h-4 w-4" />}
                            onClick={() => {
                              if (!result) return;
                              if (!isPremiumWaitlistJoined()) {
                                message.info(
                                  "AI Notes is a premium feature — join the waitlist for early access.",
                                );
                                triggerPremiumGate();
                                return;
                              }
                              summaryMutation.mutate({
                                text: result.text,
                                transcriptId: result.id,
                              });
                            }}
                            className="rounded-none bg-neutral-950 dark:bg-white dark:text-neutral-950"
                          >
                            Generate AI Notes
                          </Button>
                        </div>
                        {summaryMutation.error ? (
                          <Alert
                            className="rounded-none"
                            type="error"
                            showIcon
                            message={summaryMutation.error.message}
                          />
                        ) : null}
                        {summary ? (
                          <div className="space-y-5 border border-neutral-200 p-4 dark:border-neutral-800">
                            <div>
                              <h5 className="text-sm font-semibold">Summary</h5>
                              <p className="mt-2 leading-7 text-neutral-800 dark:text-neutral-200">
                                {summary.summary}
                              </p>
                            </div>
                            {[
                              ["Key takeaways", summary.keyPoints],
                              ["Action items", summary.actionItems],
                              ["Notes", summary.notes],
                              ["Chapters", summary.chapters],
                            ].map(([label, items]) => (
                              <div key={label as string}>
                                <h5 className="text-sm font-semibold">
                                  {label as string}
                                </h5>
                                <ul className="mt-2 space-y-2">
                                  {(items as string[]).map((point) => (
                                    <li
                                      key={point}
                                      className="border-l-2 border-neutral-950 pl-3 text-sm leading-6 dark:border-white"
                                    >
                                      {point}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                            <Button
                              onClick={() =>
                                handleCopy(summaryText, "AI notes")
                              }
                              icon={<Clipboard className="h-4 w-4" />}
                            >
                              Copy AI Notes
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    ),
                  },
                  {
                    key: "markdown",
                    label: "Markdown",
                    children: (
                      <pre className="max-h-[420px] overflow-auto border border-neutral-200 bg-neutral-50 p-4 text-xs leading-6 text-neutral-800 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200">
                        {buildTranscriptMarkdown(result)}
                      </pre>
                    ),
                  },
                ]}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
